import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import cx from 'clsx'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import Dropdown from 'components/Luxkit/Dropdown/Dropdown'
import useToggle from 'hooks/useToggle'
import Pane from 'components/Common/Pane'
import CruiseFilterFooter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseFilterFooter'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import CloseButton from 'components/Common/CloseButton/CloseButton'
import CruiseDepartureSelectList from './CruiseDepartureSelectList'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import { DeparturePortMapped, DepartureTabRegion } from './CruiseDepartureSelect'

const DropdownFooter = styled(Pane)`
  padding: ${rem(16)} 0;
  position: relative;
  box-shadow: ${props => props.theme.shadow.flat.small};
`

const TabButtonsGroupPort = styled(TabButtonsGroup)`
  width: 100%;

  &.invisible {
    opacity: 0;
    visibility: hidden;
  }
`

const SET_SECONDARY_SEARCH_ITEMS = GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEMS

interface Props {
  onChange: (values: App.CruiseGlobalFilters) => void
  dropdownAnchorRef?: React.RefObject<HTMLElement>
  regions: DepartureTabRegion[]
  setIsAllSelected: (value: boolean) => void
  isAllSelected: boolean
  setSearchTerm: (value: string) => void
  searchTerm: string
  setSelectedRegion: (value: DepartureTabRegion) => void
  selectedRegion: DepartureTabRegion
  handleSearchTerm: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleOnChange: (e: App.SearchItem[]) => void
  searchDeparturePorts: DeparturePortMapped[]
  fetching: boolean
}

function CruiseDepartureSelectDesktop({
  regions,
  onChange,
  dropdownAnchorRef,
  setIsAllSelected,
  isAllSelected,
  setSearchTerm,
  searchTerm,
  setSelectedRegion,
  selectedRegion,
  handleSearchTerm,
  handleOnChange,
  searchDeparturePorts,
  fetching,
}: Props) {
  const inputTriggerRef = useRef<HTMLButtonElement>(null)
  const { secondarySearchItems } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const [selectedRegions, setSelectedRegions] = useState<string[]>([])
  const [isOpen, toggle, show, close] = useToggle(false)

  const handleOnApply = useCallback(() => {
    onChange({ secondarySearchItems })
    toggle()
  }, [onChange, secondarySearchItems, toggle])

  const handleOnClear = useCallback(() => {
    searchDispatch({
      type: SET_SECONDARY_SEARCH_ITEMS,
      secondarySearchItems: [],
    })
    onChange({ secondarySearchItems: [] })
    setIsAllSelected(false)
    setSelectedRegions([])
  }, [onChange, searchDispatch, setIsAllSelected])

  useEffect(() => {
    if (!isOpen) {
      setSelectedRegion(regions[0])
    } else {
      setSelectedRegion(selectedRegion)
    }
  }, [regions, setSelectedRegion, isOpen, selectedRegion])

  const inputValue = useMemo(() => {
    if (isOpen) return searchTerm
    if (secondarySearchItems.length > 1) {
      const names = secondarySearchItems.slice(0, 1).map((item) => item.format.mainText).join(', ')
      return `${names} + ${secondarySearchItems.length - 1}`
    }
    return secondarySearchItems.map((item) => item.format.mainText).join(', ')
  }, [secondarySearchItems, isOpen, searchTerm])

  const handlerClearSearchTerm = useCallback(() => setSearchTerm(''), [setSearchTerm])

  return <div data-testid="cruise-departure-select-desktop">
    <SearchFormTextField
      data-testid="cruise-departure-select-desktop-input"
      label="Departing from"
      placeholder={isOpen ? 'Search departing from' : 'All departure ports'}
      value={inputValue}
      displayValue={inputValue}
      onChange={handleSearchTerm}
      onClear={handlerClearSearchTerm}
      onClick={show}
    />

    <Dropdown
      data-testid="cruise-departure-select-desktop-dropdown"
      triggerRef={inputTriggerRef}
      anchorRef={dropdownAnchorRef}
      open={isOpen}
      onClose={toggle}
      floatingStrategy="absolute"
      width="100%"
      position="bottom"
      disableFlip
    >
      <DropdownContainer fit="container">
        <LayoutContainer>
          <Group direction="vertical" gap={16}>
            <Group direction="horizontal" data-testid="cruise-departure-select-content-tab-group">
              <TabButtonsGroupPort
                className={cx({ invisible: !!searchTerm })}
              >
                {regions.map((region) => (
                  <TabButton
                    key={region.identifier}
                    size="medium"
                    isActive={region.identifier === selectedRegion.identifier}
                    onClick={() => setSelectedRegion(region)}
                  >
                    {region.name}
                  </TabButton>
                ))}
              </TabButtonsGroupPort>
              <Group direction="horizontal" fullWidth={!!searchTerm} horizontalAlign="end" verticalAlign="center">
                <CloseButton
                  data-testid="cruise-departure-select-desktop-close-button"
                  variant="dark"
                  onClick={close}
                />
              </Group>
            </Group>

            <CruiseDepartureSelectList
              searchDeparturePorts={searchDeparturePorts}
              searchTerm={searchTerm}
              secondarySearchItems={secondarySearchItems}
              setIsAllSelected={setIsAllSelected}
              isAllSelected={isAllSelected}
              onChange={handleOnChange}
              setSelectedRegions={setSelectedRegions}
              fetching={fetching}
              selectedRegion={selectedRegion}
              selectedRegions={selectedRegions}
              setSelectedRegion={setSelectedRegion}
              regions={regions}
            />
          </Group>
        </LayoutContainer>

        <DropdownFooter>
          <LayoutContainer>
            <CruiseFilterFooter
              onClear={handleOnClear}
              onApply={handleOnApply}
            />
          </LayoutContainer>
        </DropdownFooter>
      </DropdownContainer>
    </Dropdown>
  </div>
}

export default CruiseDepartureSelectDesktop
